import {
  BAY_SIDE_ALL,
  CreateLayoutImportLocationInput,
} from '@warebee/shared/data-access-api-dto';
import { RawBuilder, sql } from 'kysely';
import { makeMappingError, MappingSchema } from '../mapping';
import { locationStatusResolver } from '../valueResolver/booleanValueResolver';
import { baySideValueResolver } from '../valueResolver/enumValueResolver';

export const LAYOUT_IMPORT_MAPPING_SCHEMA: MappingSchema<CreateLayoutImportLocationInput> =
  {
    fields: [
      {
        name: 'locationId',
        type: 'string',
        unique: true,
      },
      {
        name: 'locationOrder',
        type: 'bigint',
      },
      {
        name: 'locationStatus',
        type: 'boolean',
        defaultValueResolver: locationStatusResolver,
      },
      {
        name: 'locationLevel',
        type: 'integer',
      },
      {
        name: 'warehouseArea',
        type: 'string',
      },
      {
        name: 'locationLength',
        type: 'number',
        measureType: 'size',
      },
      {
        name: 'locationWidth',
        type: 'number',
        measureType: 'size',
      },
      {
        name: 'locationHeight',
        type: 'number',
        measureType: 'size',
      },
      {
        name: 'locationWeight',
        type: 'number',
        measureType: 'weight',
      },
      {
        name: 'locationAisle',
        type: 'string',
      },
      {
        name: 'locationBay',
        type: 'integer',
      },
      {
        name: 'locationBayPosition',
        type: 'integer',
      },
      {
        name: 'baySide',
        type: { enumValues: BAY_SIDE_ALL },
        defaultValueResolver: baySideValueResolver,
      },
      {
        name: 'locationUsageType',
        type: 'string',
      },
      {
        name: 'locationRackingType',
        type: 'string',
      },
      {
        name: 'locationDepthPosition',
        type: 'integer',
        optional: true,
      },
      {
        name: 'locmhtype',
        type: 'string',
        optional: true,
      },
      {
        name: 'bayType',
        type: 'string',
        optional: true,
      },
      {
        name: 'primaryAccessAisle',
        type: 'string',
        optional: true,
      },
      {
        name: 'secondaryAccessAisle',
        type: 'string',
        optional: true,
      },
      {
        name: 'congestionZone',
        type: 'string',
        optional: true,
      },
    ],
    createValidationQueries: (
      src: RawBuilder<any>,
      lineNumberRef: RawBuilder<any>,
    ) => [
      sql`
        select
          ${makeMappingError({
            code: sql.lit('BusinessLogicValidationError'),
            type: sql.lit('Bay Type validation error'),
            property: sql.lit('bayType'),
            message: sql`'Multiple ''Bay Types'' assigned to the same Bay ID: ' || cast(location_bay as varchar) || ' at Aisle: ' || location_aisle || ' Side: ' || bay_side || ' Area: ' || warehouse_area || ' Bay Type(s): ' || array_join(array_agg(distinct bay_type), ', ')`,
            lines: sql`array_agg(${lineNumberRef})`,
          })}
        from ${src}
        group by warehouse_area, location_aisle, bay_side, location_bay
        having count(distinct bay_type) > 1

        UNION 

        select
          ${makeMappingError({
            code: sql.lit('BusinessLogicValidationError'),
            type: sql.lit('Location length validation error'),
            value: sql.lit('locationLength'),
            property: sql.lit('locationLength'),
            message: sql.lit('Location length should be greater than zero'),
            line: lineNumberRef,
          })}
        from ${src}
        where location_length <= 0

        UNION 

        select
          ${makeMappingError({
            code: sql.lit('BusinessLogicValidationError'),
            type: sql.lit('Location width validation error'),
            value: sql.lit('locationWidth'),
            property: sql.lit('locationWidth'),
            message: sql.lit('Location width should be greater than zero'),
            line: lineNumberRef,
          })}
        from ${src}
        where location_width <= 0

        UNION 

        select
          ${makeMappingError({
            code: sql.lit('BusinessLogicValidationError'),
            type: sql.lit('Location height validation error'),
            value: sql.lit('locationHeight'),
            property: sql.lit('locationHeight'),
            message: sql.lit('Location height should be greater than zero'),
            line: lineNumberRef,
          })}
        from ${src}
        where location_height <= 0`,
    ],
    primaryKey: ['locationId'],
    appendable: true,
  };
